import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { notify } from './Notifier';

/**
 * Phải khớp ALLOWED_DOCUMENT_MIME_TYPES / MAX_DOCUMENT_BYTES ở
 * apps/api/src/lib/r2.ts. Lệch nhau thì người dùng hoặc bị chặn oan ở
 * client, hoặc chọn xong mới ăn 400 lúc tải lên.
 */
export const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/markdown',
  'application/zip',
  'application/x-zip-compressed',
];
export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function fileIconFor(mime: string): JSX.Element {
  if (mime.startsWith('image/')) return <ImageIcon fontSize="small" />;
  if (mime === 'application/pdf') return <PictureAsPdfIcon fontSize="small" />;
  return <InsertDriveFileIcon fontSize="small" />;
}

interface FileAttachInputProps {
  /** Danh sách file CHƯA gửi lên server — cha giữ state và tự upload khi lưu. */
  value: File[];
  onChange: (files: File[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  /** Thu gọn thành vùng kéo-thả nhỏ (vẫn hiện dropzone, chỉ co kích thước). */
  compact?: boolean;
  /**
   * Thu gọn thành một nút kẹp giấy thay cho vùng kéo-thả, giống ImageUploader
   * ở ô bình luận của bug. Ưu tiên hơn `compact` khi cả hai cùng true.
   */
  iconButton?: boolean;
}

/**
 * Vùng chọn file đính kèm dùng chung (yêu cầu chức năng): bấm chọn hoặc
 * kéo-thả, kiểm tra MIME/dung lượng, hiển thị danh sách file đã chọn trước
 * khi gửi. Khác ImageUploader: nhận mọi loại tài liệu (PDF/Office/zip...),
 * không riêng ảnh, và không có bản xem trước dạng thumbnail.
 */
export function FileAttachInput({
  value,
  onChange,
  multiple = true,
  disabled = false,
  compact = false,
  iconButton = false,
}: FileAttachInputProps): JSX.Element {
  const [dragOver, setDragOver] = useState(false);
  const [viewing, setViewing] = useState<{ file: File; url: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filesRef = useRef(value);
  filesRef.current = value;

  const validateAndAdd = useCallback(
    (incoming: FileList | File[]) => {
      const accepted: File[] = [];
      let rejected = 0;

      for (const file of Array.from(incoming)) {
        if (!ACCEPTED_FILE_TYPES.includes(file.type) || file.size > MAX_FILE_BYTES) {
          rejected += 1;
          continue;
        }
        accepted.push(file);
      }

      if (rejected > 0) {
        notify(
          `${rejected} tệp bị bỏ qua (định dạng không hỗ trợ hoặc vượt quá ${MAX_FILE_BYTES / (1024 * 1024)}MB)`,
          'warning',
        );
      }
      if (accepted.length > 0) {
        onChange(multiple ? [...filesRef.current, ...accepted] : accepted);
      }
    },
    [multiple, onChange],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      if (e.dataTransfer.files?.length) validateAndAdd(e.dataTransfer.files);
    },
    [disabled, validateAndAdd],
  );

  const removeAt = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const previews = useMemo(
    () =>
      value.map((file, index) => ({
        file,
        index,
        isImage: file.type.startsWith('image/'),
        url: URL.createObjectURL(file),
      })),
    [value],
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  const imagePreviews = useMemo(() => previews.filter((p) => p.isImage), [previews]);
  const otherPreviews = useMemo(() => previews.filter((p) => !p.isImage), [previews]);

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPTED_FILE_TYPES.join(',')}
      multiple={multiple}
      hidden
      disabled={disabled}
      onChange={(e) => {
        if (e.target.files?.length) validateAndAdd(e.target.files);
        e.target.value = '';
      }}
    />
  );

  return (
    <Box>
      {iconButton ? (
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <Tooltip title="Đính kèm file">
            <span>
              <IconButton
                data-testid="file-attach-button"
                size="small"
                color={dragOver ? 'primary' : 'default'}
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
              >
                <AttachFileIcon fontSize="small" />
              </IconButton>
              {fileInput}
            </span>
          </Tooltip>
        </Box>
      ) : (
        <Box
          data-testid="file-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && inputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            borderRadius: 2,
            bgcolor: dragOver ? 'action.hover' : 'transparent',
            p: compact ? 1 : 3,
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            transition: 'border-color 0.15s, background-color 0.15s',
          }}
        >
          <CloudUploadIcon color="action" sx={{ fontSize: compact ? 22 : 36, mb: compact ? 0.25 : 1 }} />
          <Typography variant={compact ? 'caption' : 'body2'} color="text.secondary" sx={{ display: 'block' }}>
            Kéo thả, dán, hoặc bấm để chọn file
          </Typography>
          {!compact && (
            <Typography variant="caption" color="text.secondary">
              Ảnh, PDF, Word, Excel, PowerPoint, zip — tối đa {MAX_FILE_BYTES / (1024 * 1024)}MB mỗi file
            </Typography>
          )}
          {fileInput}
        </Box>
      )}

      {imagePreviews.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={iconButton ? 0.75 : 1.5} sx={{ mt: 1.5 }} data-testid="file-image-previews">
          {imagePreviews.map((p) => (
            <Box
              key={`${p.file.name}-${p.index}`}
              sx={{
                position: 'relative',
                width: iconButton ? 48 : compact ? 72 : 96,
                height: iconButton ? 48 : compact ? 72 : 96,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                component="img"
                src={p.url}
                alt={p.file.name}
                onClick={() => setViewing(p)}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }}
              />
              <IconButton
                aria-label={`Bỏ ảnh ${p.file.name}`}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(p.index);
                }}
                disabled={disabled}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  p: iconButton ? 0 : 0.25,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                }}
              >
                <CloseIcon sx={{ fontSize: iconButton ? 14 : 18 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      {otherPreviews.length > 0 && (
        <Stack spacing={0.75} sx={{ mt: 1.5 }} data-testid="file-previews">
          {otherPreviews.map((p) => (
            <Stack
              key={`${p.file.name}-${p.index}`}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5 }}
            >
              {fileIconFor(p.file.type)}
              <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                {p.file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(p.file.size)}
              </Typography>
              <IconButton
                aria-label={`Bỏ file ${p.file.name}`}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  removeAt(p.index);
                }}
                disabled={disabled}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Dialog open={viewing !== null} onClose={() => setViewing(null)} maxWidth="md">
        {viewing && (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              aria-label="Đóng"
              onClick={() => setViewing(null)}
              sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
            <Box component="img" src={viewing.url} alt={viewing.file.name} sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh' }} />
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
